<template>
  <a-modal
    v-if="visible"
    width="800px"
    :visible="visible"
    title="Written Warning History"
    @cancel="closeModal"
    data-testid="write-up-history-modal"
  >
    <a-modal
      v-if="visibleStatistic"
      title="Statistic"
      :visible="visibleStatistic"
      :destroy-on-close="true"
      width="600px"
      @cancel="visibleStatistic = false"
      data-testid="write-up-history-statistic-modal"
      ><div class="grid m-0" data-testid="write-up-history-statistic-content">
        <div class="col-6">
          <h4>Written Warning By Status</h4>
          <bar-chart class="chart" :chart-data="statusChartData" />
        </div>
        <div class="col-6">
          <h4>Written Warning By Violation Types</h4>
          <bar-chart class="chart" :chart-data="authTypeChartData" />
        </div>
      </div>
      <template slot="footer">
        <div class="flex justify-content-between" data-testid="write-up-history-statistic-footer">
          <a-button @click="visibleStatistic = false" data-testid="write-up-history-statistic-close-button">Close</a-button>
        </div>
      </template></a-modal
    >

    <create-write-up :visible.sync="visibleCreateWriteup" />
    <sign-write-up :visible.sync="visibleSignModal" :writeup_id.sync="writeupId" />

    <div v-if="loading" class="loading-spin" data-testid="write-up-history-loading">
      <a-spin />
    </div>

    <template v-else
      ><div class="pt-3 text-center" v-if="writeups.length === 0 && !loading" data-testid="write-up-history-empty-state">
        <h3>No Written Warning Found.</h3>
      </div>
      <div class="list" v-else data-testid="write-up-history-list">
        <div class="item" v-for="(item, index) in writeups" :key="item.id" data-testid="write-up-history-item">
          <div class="flex justify-content-between">
            <h4>№ {{ index + 1 }} from {{ formatDate(item.create_date) }}</h4>
            <div class="flex gap-2">
              <a-button
                :loading="loadingDownloadButtons[item.id]"
                icon="download"
                type="primary"
                @click="download([item.id], false)"
                data-testid="write-up-history-item-download-button"
              >
                Download PDF
              </a-button>
              <a-button
                v-if="item.status !== 'signed'"
                @click="openSignModal(item.id)"
                type="primary"
                data-testid="write-up-history-item-refuse-button"
              >
                Refuse to Acknowledge
              </a-button>
              <a-button
                v-if="
                  hasUncontrolledPermission('manage_associates', {
                    id: associate?.id
                  })
                "
                :loading="loadingButtons[item.id]"
                @click="cancelWriteUp(item.id)"
                type="danger"
                data-testid="write-up-history-item-cancel-button"
              >
                Cancel
              </a-button>
            </div>
          </div>
          <a-descriptions :column="1" size="small">
            <a-descriptions-item label="Status">
              <a-badge :color="getStatus(item.status).color" :text="getStatus(item.status).label" />
            </a-descriptions-item>
            <a-descriptions-item label="Violation">
              <a-tag class="m-0" v-if="item.authorization" :color="getAuthStatus(item.authorization).color">
                <span>{{ getAuthStatus(item.authorization).label }}</span>
              </a-tag>
              <span v-else>-</span>
            </a-descriptions-item>
            <a-descriptions-item label="Author">
              {{ item?.author?.first_name }}
              {{ item?.author?.last_name }}
            </a-descriptions-item>
            <a-descriptions-item label="Violation category">
              {{ getViolationTitle(item.violation_category) }}
            </a-descriptions-item>
            <a-descriptions-item label="Reason for action">
              {{ item.reason_for_action || ' - ' }}
            </a-descriptions-item>
            <a-descriptions-item label="Expected level of performance">
              {{ item.expected_level_of_performance || ' - ' }}
            </a-descriptions-item>
            <a-descriptions-item label="Days until the follow-up review date">
              {{ item.follow_up_review_date_days || ' - ' }}
            </a-descriptions-item>
            <a-descriptions-item label="Attachments">
              <attachments-field :files="item.attached_files" :is-view="true" :with-title="false" />
            </a-descriptions-item>
            <a-descriptions-item label="Violated At">
              {{ getDateTime(item.violated_at) }}
            </a-descriptions-item>
            <a-descriptions-item label="Created At">
              {{ getDateTime(item.create_date) }}
            </a-descriptions-item>
          </a-descriptions>
        </div>
      </div></template
    >
    <template slot="footer">
      <div class="flex justify-content-between" data-testid="write-up-history-footer">
        <a-button @click="closeModal" data-testid="write-up-history-close-button"> Cancel </a-button>
        <div class="flex gap-2" data-testid="write-up-history-actions">
          <a-button
            :loading="loadingDownload"
            :icon="associateProfileExport ? 'export' : 'download'"
            type="primary"
            v-if="writeups.length"
            @click="handleExportAll"
            data-testid="write-up-history-download-all-button"
          >
            {{ associateProfileExport ? 'Export' : 'Download PDF' }}
          </a-button>
          <a-button
            v-if="
              hasUncontrolledPermission('manage_associates', {
                id: associate?.id
              })
            "
            icon="plus"
            type="primary"
            @click="visibleCreateWriteup = true"
            data-testid="write-up-history-create-button"
          >
            Create Written Warning </a-button
          ><a-button
            icon="eye"
            type="primary"
            @click="visibleStatistic = true"
            data-testid="write-up-history-open-statistic-button"
          >
            View Statistic
          </a-button>
        </div>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { Badge, Button, Descriptions, Modal, Spin, Tag, notification } from 'ant-design-vue'
import BarChart from '@/components/ui/bar-chart.vue'
import { authorizationsMap, writeUpStatuses, violationCategories } from '@/const'
import moment from 'moment-timezone'
import util from '@/util'
import { createNamespacedHelpers } from 'vuex'
import ShiftFormAttachments from '@/components/form-attachments.vue'
import CreateWriteUp from '@/modules/profile/components/create-write-up.vue'
import ProfileWriteUpSign from '@/modules/profile/components/profile-write-up-sign.vue'
import permissionMixin from '@/mixins/permissionMixin'

const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')

export default {
  data() {
    return {
      loading: false,
      loadingDownload: false,

      loadingButtons: {},
      loadingDownloadButtons: {},

      visibleCreateWriteup: false,
      visibleSignModal: false,
      visibleStatistic: false,

      writeupId: undefined
    }
  },
  mixins: [permissionMixin],
  components: {
    'a-button': Button,
    'a-descriptions': Descriptions,
    'a-descriptions-item': Descriptions.Item,
    'a-tag': Tag,
    'a-badge': Badge,
    'a-spin': Spin,
    'a-modal': Modal,
    'attachments-field': ShiftFormAttachments,
    'create-write-up': CreateWriteUp,
    'bar-chart': BarChart,
    'sign-write-up': ProfileWriteUpSign
  },
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    associateNumber: {
      type: String,
      default: null
    },
    associateProfileExport: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate,
      writeups: state =>
        (state.associateWriteUps ?? []).slice().sort((a, b) => moment(b.create_date) - moment(a.create_date))
    }),

    statusChartData() {
      return this.createChartData(writeUpStatuses, 'status')
    },

    authTypeChartData() {
      return this.createChartData(authorizationsMap, 'authorization')
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          this.fetchWriteUps()
        }
      },
      immediate: true
    }
  },
  methods: {
    ...profileActions(['postCancelWriteUp', 'downloadAssociateWriteUps', 'getWriteUps', 'getAssociate']),

    closeModal() {
      this.$emit('update:visible', false)
      this.$emit('update:associateNumber', undefined)
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    formatDate(date) {
      return moment(date).format('MMM D hh:mm A')
    },

    createChartData(map, keyField) {
      const itemCount = this.writeups.reduce((acc, item) => {
        acc[item[keyField]] = (acc[item[keyField]] || 0) + 1
        return acc
      }, {})

      const { labels, colors, data } = Object.entries(map).reduce(
        (acc, [key, { label, color }]) => {
          if (itemCount[key]) {
            acc.labels.push(label)
            acc.colors.push(color)
            acc.data.push(itemCount[key])
          }
          return acc
        },
        { labels: [], colors: [], data: [] }
      )

      return {
        labels,
        datasets: [
          {
            label: 'Written Warnings Status Count',
            backgroundColor: colors,
            data
          }
        ]
      }
    },

    getStatus(value) {
      const status = writeUpStatuses[value]

      return { label: status?.label || '', color: status?.color || '#fff' }
    },

    getAuthStatus(value) {
      const status = authorizationsMap[value]

      return { label: status?.label || '', color: status?.color || '#fff' }
    },

    getViolationTitle(status) {
      return violationCategories.find(item => item.key === status)?.label
    },

    openSignModal(id) {
      this.writeupId = id
      this.visibleSignModal = true
    },
    getDateTime(date) {
      if (!date) return '-'

      return util.convertDateTimeToCurrentTimeZoneWithFormat(date, 'MM/DD/YYYY')
    },

    async cancelWriteUp(id) {
      this.$set(this.loadingButtons, id, true)

      try {
        await this.postCancelWriteUp({ writeup: id })

        this.showNotification('success', 'Success', 'Successfully canceled the written warning.')
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.$set(this.loadingButtons, id, false)
      }
    },

    async download(id, all) {
      if (all) {
        this.loadingDownload = true
      } else {
        this.$set(this.loadingDownloadButtons, id, true)
      }

      try {
        const url = await this.downloadAssociateWriteUps({
          id
        })

        util.downloadFile(url)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      } finally {
        if (all) {
          this.loadingDownload = false
        } else {
          this.$set(this.loadingDownloadButtons, id, false)
        }
      }
    },
    async fetchWriteUps() {
      if (!this.associateNumber) {
        return
      }

      this.loading = true
      try {
        const payload = {
          associate_number: this.associateNumber,
          project_id: this.project_id
        }

        await Promise.all([this.getAssociate(payload), this.getWriteUps(payload)])
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loading = false
      }
    },
    handleExportAll() {
      if (this.associateProfileExport) {
        this.closeModal()
        this.$emit('export')
      return
      }

      this.download(this.writeups.map(item => item.id), true)
    }
  }
}
</script>

<style scoped>
::v-deep .ant-modal-body {
  padding-top: 0;
  min-height: 200px;
  max-height: 600px;
  overflow-y: scroll;
}

::v-deep .ant-modal-content {
  overflow: hidden;
}

::v-deep .ant-descriptions-item-colon {
  width: 50%;
}

::v-deep .ant-tabs-content {
  height: 480px !important;
  padding: 0 !important;
  overflow-y: visible !important;
}

::v-deep .ant-tabs-content .ant-tabs-tabpane {
  overflow-y: auto;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.item {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border: 1px solid var(--border-color-base);
  border-radius: 4px;
}

.chart {
  width: 80%;
  margin-bottom: 10px;
}
</style>
